import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize, firstValueFrom} from 'rxjs';
import {MessageService} from 'primeng/api';
import {Button} from 'primeng/button';
import {Checkbox} from 'primeng/checkbox';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {Textarea} from 'primeng/textarea';

import {SupportEmailClient} from '../../api-client/api/api';
import {SupportReplyCategoryDto} from '../../api-client/model/support-reply-category-dto';
import {SupportReplyConditionDto} from '../../api-client/model/support-reply-condition-dto';
import {SupportReplySettingsDto} from '../../api-client/model/support-reply-settings-dto';
import {SupportReplyVariableCaseDto} from '../../api-client/model/support-reply-variable-case-dto';
import {SupportReplyVariableDto} from '../../api-client/model/support-reply-variable-dto';
import {SubmitButtonComponent} from '../../shared/components/submit-button/submit-button.component';
import {DATE_TIME_FORMAT} from '../../shared/formats';

// Mirror the API's limits, so the page stops where a save would fail.
const MAX_CATEGORIES = 20;
const MAX_TITLE_LENGTH = 80;
const MAX_EXAMPLES = 10;
const MAX_WHEN_TO_USE_LENGTH = 2000;
const MAX_EXAMPLE_LENGTH = 1500;
const MAX_TEMPLATE_LENGTH = 5000;
const MAX_VARIABLES = 30;
const MAX_CASES = 20;
const MAX_CASE_TEXT_LENGTH = 2000;
const MAX_DESCRIPTION_LENGTH = 500;
const VARIABLE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9]{0,39}$/;

const NEW_CATEGORY_TEMPLATE = '{greeting}\n\n\n\nBest,\nThe Homey team';

// The id the classifier answers with, from the title: "Refund request" becomes "refund-request".
// Mirrors the API's pattern, and leaves "other" alone.
function labelFromTitle(title: string, taken: readonly string[]): string {
  const base =
    title
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^[^a-z]+/, '')
      .slice(0, 36)
      .replace(/-+$/, '') || 'category';
  let label = base;
  for (let n = 2; label === 'other' || taken.includes(label); n++) {
    label = `${base}-${n}`;
  }
  return label;
}

function textareaValue(event: Event): string {
  return event.target instanceof HTMLTextAreaElement ? event.target.value : '';
}

function variableToken(name: string): string {
  return `{${name}}`;
}

function replaceAt<T>(items: readonly T[], index: number, update: (item: T) => T): T[] {
  return items.map((item, i) => (i === index ? update(item) : item));
}

function removeAt<T>(items: readonly T[], index: number): T[] {
  return items.filter((_, i) => i !== index);
}

const PLACEHOLDER_PATTERN = /\{[^{}]*\}/g;

function unknownPlaceholders(text: string, known: readonly string[]): string[] {
  const used = new Set(text.match(PLACEHOLDER_PATTERN) ?? []);
  return [...used].filter(token => !known.includes(token));
}

// A brace outside a complete placeholder, as in "{greeting" or "{{greeting}}", would reach the
// reply as literal text. Mirrors the API's check.
function hasStrayBraces(text: string): boolean {
  return /[{}]/.test(text.replace(PLACEHOLDER_PATTERN, ''));
}

function failed(checks: readonly [boolean, string][]): string[] {
  return checks.filter(([fails]) => fails).map(([, message]) => message);
}

// Used by the categories and by Other.
function whenToUseChecks(whenToUse: string): [boolean, string][] {
  return [
    [whenToUse.trim() === '', '"When to use" is empty.'],
    [whenToUse.length > MAX_WHEN_TO_USE_LENGTH, `"When to use" is longer than ${MAX_WHEN_TO_USE_LENGTH} characters.`],
  ];
}

function categoryProblems(category: SupportReplyCategoryDto, variableTokens: readonly string[]): string[] {
  const unknown = unknownPlaceholders(category.template, variableTokens);
  return failed([
    [category.title.trim() === '', 'The title is empty.'],
    [category.title.length > MAX_TITLE_LENGTH, `The title is longer than ${MAX_TITLE_LENGTH} characters.`],
    ...whenToUseChecks(category.whenToUse),
    [category.examples.some(example => example.trim() === ''), 'An example email is empty.'],
    [category.examples.some(example => example.length > MAX_EXAMPLE_LENGTH), `An example email is longer than ${MAX_EXAMPLE_LENGTH} characters.`],
    [category.template.trim() === '', 'The reply template is empty.'],
    [category.template.length > MAX_TEMPLATE_LENGTH, `The reply template is longer than ${MAX_TEMPLATE_LENGTH} characters.`],
    [unknown.length > 0, `Unknown variable ${unknown.join(', ')}.`],
    [hasStrayBraces(category.template), 'The reply template has a { or } that is not part of a variable.'],
  ]);
}

// Mirrors findVariableProblems in support-email-domain.
function variableProblems(
  variable: SupportReplyVariableDto,
  index: number,
  variables: readonly SupportReplyVariableDto[],
  fieldsByCondition: ReadonlyMap<SupportReplyConditionDto, readonly string[]>
): string[] {
  const lowerName = variable.name.toLowerCase();
  const fieldTokens = [...fieldsByCondition.values()].flat();
  const caseProblems = variable.cases.flatMap((variableCase, caseIndex) => {
    if (variableCase.noDraft) {
      return [];
    }
    const unknown = unknownPlaceholders(variableCase.text, fieldsByCondition.get(variableCase.when) ?? []);
    return failed([
      [variableCase.text.trim() === '', `Case ${caseIndex + 1}: the text is empty.`],
      [variableCase.text.length > MAX_CASE_TEXT_LENGTH, `Case ${caseIndex + 1}: the text is longer than ${MAX_CASE_TEXT_LENGTH} characters.`],
      [unknown.length > 0, `Case ${caseIndex + 1}: ${unknown.join(', ')} can't be used with this condition.`],
      [hasStrayBraces(variableCase.text), `Case ${caseIndex + 1}: a { or } is not part of a field.`],
    ]);
  });
  return [
    ...failed([
      [!VARIABLE_NAME_PATTERN.test(variable.name), 'The name must be a letter followed by up to 39 letters or digits.'],
      [variables.findIndex(other => other.name.toLowerCase() === lowerName) !== index, 'Another variable has this name.'],
      [variable.description.length > MAX_DESCRIPTION_LENGTH, `The description is longer than ${MAX_DESCRIPTION_LENGTH} characters.`],
      [fieldTokens.includes(variableToken(variable.name)), 'This name is an account field.'],
      [variable.cases.at(-1)?.when !== 'always', 'The last case must be "Always".'],
    ]),
    ...caseProblems,
  ];
}

@Component({
  selector: 'homey-support-email-page',
  standalone: true,
  imports: [DatePipe, FormsModule, RouterLink, Button, Checkbox, InputText, Select, Textarea, SubmitButtonComponent],
  templateUrl: './support-email-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportEmailPageComponent {
  private readonly supportEmailClient = inject(SupportEmailClient);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal(true);
  protected readonly settings = signal<SupportReplySettingsDto | undefined>(undefined);
  protected readonly categories = signal<readonly SupportReplyCategoryDto[]>([]);
  protected readonly variables = signal<readonly SupportReplyVariableDto[]>([]);
  protected readonly otherWhenToUse = signal('');
  protected readonly dateTimeFormat = DATE_TIME_FORMAT;
  protected readonly maxCategories = MAX_CATEGORIES;
  protected readonly maxExamples = MAX_EXAMPLES;
  protected readonly maxVariables = MAX_VARIABLES;
  protected readonly maxCases = MAX_CASES;

  protected readonly conditions = computed(() => this.settings()?.conditions ?? []);

  protected readonly fieldsByCondition = computed(
    () => new Map(this.conditions().map(info => [info.condition, info.fields] as const))
  );

  // Indexed like categories(): a new category has no id until it is saved.
  protected readonly problemsByCategory = computed(() => {
    const tokens = this.variables().map(variable => variableToken(variable.name));
    return this.categories().map(category => categoryProblems(category, tokens));
  });

  protected readonly otherProblems = computed(() => failed(whenToUseChecks(this.otherWhenToUse())));

  // Indexed like variables(): names change while they are typed.
  protected readonly problemsByVariable = computed(() => {
    const variables = this.variables();
    return variables.map((variable, index) => variableProblems(variable, index, variables, this.fieldsByCondition()));
  });

  protected readonly sectionsWithProblems = computed(
    () =>
      [...this.problemsByCategory(), this.otherProblems(), ...this.problemsByVariable()].filter(
        problems => problems.length > 0
      ).length
  );

  constructor() {
    this.loadSettings();
  }

  protected addCategory(): void {
    this.categories.update(categories => [
      ...categories,
      {label: '', title: '', whenToUse: '', examples: [], template: NEW_CATEGORY_TEMPLATE},
    ]);
  }

  protected removeCategory(index: number): void {
    this.categories.update(categories => removeAt(categories, index));
  }

  protected setTitle(index: number, title: string): void {
    this.updateCategory(index, category => ({...category, title}));
  }

  protected setWhenToUse(index: number, event: Event): void {
    this.updateCategory(index, category => ({...category, whenToUse: textareaValue(event)}));
  }

  protected setTemplate(index: number, event: Event): void {
    this.updateCategory(index, category => ({...category, template: textareaValue(event)}));
  }

  protected setExample(index: number, exampleIndex: number, event: Event): void {
    const value = textareaValue(event);
    this.updateCategory(index, category => ({
      ...category,
      examples: replaceAt(category.examples, exampleIndex, () => value),
    }));
  }

  protected addExample(index: number): void {
    this.updateCategory(index, category => ({...category, examples: [...category.examples, '']}));
  }

  protected removeExample(index: number, exampleIndex: number): void {
    this.updateCategory(index, category => ({...category, examples: removeAt(category.examples, exampleIndex)}));
  }

  protected setOtherWhenToUse(event: Event): void {
    this.otherWhenToUse.set(textareaValue(event));
  }

  protected addVariable(): void {
    this.variables.update(variables => [
      ...variables,
      {name: '', description: '', cases: [{when: 'always', noDraft: false, text: ''}]},
    ]);
  }

  protected removeVariable(index: number): void {
    this.variables.update(variables => removeAt(variables, index));
  }

  protected setVariableName(index: number, name: string): void {
    this.updateVariable(index, variable => ({...variable, name}));
  }

  protected setVariableDescription(index: number, description: string): void {
    this.updateVariable(index, variable => ({...variable, description}));
  }

  // New cases go above the last one, which has to stay "Always".
  protected addCase(index: number): void {
    this.updateVariable(index, variable => ({
      ...variable,
      cases: [
        ...variable.cases.slice(0, -1),
        {when: 'has-first-name', noDraft: false, text: ''},
        ...variable.cases.slice(-1),
      ],
    }));
  }

  protected removeCase(index: number, caseIndex: number): void {
    this.updateVariable(index, variable => ({...variable, cases: removeAt(variable.cases, caseIndex)}));
  }

  protected moveCase(index: number, caseIndex: number, step: -1 | 1): void {
    this.updateVariable(index, variable => {
      const cases = [...variable.cases];
      [cases[caseIndex], cases[caseIndex + step]] = [cases[caseIndex + step], cases[caseIndex]];
      return {...variable, cases};
    });
  }

  protected setCaseCondition(index: number, caseIndex: number, when: SupportReplyConditionDto): void {
    this.updateCase(index, caseIndex, variableCase => ({...variableCase, when}));
  }

  protected setCaseNoDraft(index: number, caseIndex: number, noDraft: boolean): void {
    this.updateCase(index, caseIndex, variableCase => ({...variableCase, noDraft}));
  }

  protected setCaseText(index: number, caseIndex: number, event: Event): void {
    this.updateCase(index, caseIndex, variableCase => ({...variableCase, text: textareaValue(event)}));
  }

  protected readonly save = async (): Promise<void> => {
    // A new category gets its id from its title when it is first saved, and keeps it after.
    const taken = this.categories().map(category => category.label);
    const assigned: string[] = [];
    const categories = this.categories().map(category => {
      if (category.label) {
        return category;
      }
      const label = labelFromTitle(category.title, [...taken, ...assigned]);
      assigned.push(label);
      return {...category, label};
    });
    this.categories.set(categories);

    const saved = await firstValueFrom(
      this.supportEmailClient.updateSupportReplySettings({
        categories,
        otherWhenToUse: this.otherWhenToUse(),
        variables: [...this.variables()],
      })
    ).catch((error: unknown) => {
      // Not saved, so not set yet: the next save makes the id from the title as it is then.
      this.categories.update(current =>
        current.map(category => (assigned.includes(category.label) ? {...category, label: ''} : category))
      );
      throw error;
    });
    // Only the "last saved" line is refreshed: the server returns what was sent, and replacing
    // the form with it would undo anything typed while the save was in flight.
    this.settings.set(saved);
    this.messageService.add({severity: 'success', summary: 'Support email settings saved'});
  };

  private updateCategory(index: number, update: (category: SupportReplyCategoryDto) => SupportReplyCategoryDto): void {
    this.categories.update(categories => replaceAt(categories, index, update));
  }

  private updateVariable(index: number, update: (variable: SupportReplyVariableDto) => SupportReplyVariableDto): void {
    this.variables.update(variables => replaceAt(variables, index, update));
  }

  private updateCase(
    index: number,
    caseIndex: number,
    update: (variableCase: SupportReplyVariableCaseDto) => SupportReplyVariableCaseDto
  ): void {
    this.updateVariable(index, variable => ({...variable, cases: replaceAt(variable.cases, caseIndex, update)}));
  }

  private loadSettings(): void {
    this.supportEmailClient
      .getSupportReplySettings()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(settings => {
        this.settings.set(settings);
        this.categories.set(settings.categories);
        this.otherWhenToUse.set(settings.otherWhenToUse);
        this.variables.set(settings.variables);
      });
  }
}
